import { Group, LineSegments, Vector3 } from 'three';

import type ViewPort from '../ViewPort';
import { getOrCreateThreeColor } from '../colors';
import { UNITS } from '../constants';
import { getLineMaterial } from '../materials';
import { MeasurementObject } from '../renderer/measurement-utils';
import type { OptimalBoundingBoxGeometry } from './OptimalBoundingBoxGeometry';

const defaultLengthColor = getOrCreateThreeColor(0xff0000);
const defaultWidthColor = getOrCreateThreeColor(0x00ff00);
const defaultHeightColor = getOrCreateThreeColor(0x0000ff);

//Reuse boundingBoxMaterial because it does not change
const boundingBoxMaterial = getLineMaterial(getOrCreateThreeColor(0x348feb), 2);

export class OptimalBoundingBox {
  public readonly id: string;
  public readonly box: LineSegments;
  public readonly lengthObject: MeasurementObject;
  public readonly group = new Group();
  public readonly widthObject: MeasurementObject;
  public readonly heightObject: MeasurementObject;
  public readonly min: Vector3;
  private _paintParams = '';
  private isDisposed?: boolean;

  constructor(public readonly geometry: OptimalBoundingBoxGeometry) {
    this.id = Math.random().toString(32).slice(2);

    this.box = new LineSegments(this.geometry.geometry, boundingBoxMaterial);

    const min = new Vector3().copy(this.geometry.min);

    this.lengthObject = new MeasurementObject({
      startPoint: new Vector3().copy(min),
      endPoint: new Vector3()
        .copy(min)
        .add(
          new Vector3()
            .copy(this.geometry.lengthDirection)
            .multiplyScalar(this.geometry.length),
        ),
      lineColor: defaultLengthColor,
      sphereColor: defaultLengthColor,
    });
    this.lengthObject.group.visible = true;

    this.widthObject = new MeasurementObject({
      startPoint: new Vector3().copy(min),
      endPoint: new Vector3()
        .copy(min)
        .add(
          new Vector3()
            .copy(this.geometry.widthDirection)
            .multiplyScalar(this.geometry.width),
        ),
      lineColor: defaultWidthColor,
      sphereColor: defaultWidthColor,
    });
    this.widthObject.group.visible = true;

    this.heightObject = new MeasurementObject({
      startPoint: new Vector3().copy(min),
      endPoint: new Vector3()
        .copy(min)
        .add(
          new Vector3()
            .copy(this.geometry.heightDirection)
            .multiplyScalar(this.geometry.height),
        ),
      lineColor: defaultHeightColor,
      sphereColor: defaultHeightColor,
    });
    this.heightObject.group.visible = true;

    this.min = min;

    this.group.add(
      this.box,
      this.lengthObject.group,
      this.widthObject.group,
      this.heightObject.group,
    );
    this.group.visible = false;
    this.group.name = this.id;
  }

  public dispose() {
    if (this.isDisposed) return;
    this.isDisposed = true;
    for (const o of [this.lengthObject, this.widthObject, this.heightObject]) {
      o.dispose();
    }

    this.box.clear();
    //Don't dispose this.box.geometry because it is shared and belongs to part
    this.box.removeFromParent();

    this.group.clear();
    this.group.removeFromParent();
  }

  public makeSpriteMaterial({
    displayUnits = UNITS.IN,
    nativeUnits = UNITS.MM,
    dimensionalPrecision = 3,
    displayScale = 1.0,
    backgroundFillStyle = 'rgb(181, 217, 255, 0.5)',
  }: {
    displayUnits: UNITS;
    nativeUnits: UNITS;
    dimensionalPrecision: number;
    displayScale: number;
    backgroundFillStyle?: string;
  }): void {
    this.lengthObject.makeSpriteMaterial({
      displayUnits,
      nativeUnits,
      dimensionalPrecision,
      displayScale,
      messageFillStyle: 'black',
      messageHeader: 'L: ',
      messageHeaderFillStyle: 'red',
      backgroundFillStyle,
    });
    this.widthObject.makeSpriteMaterial({
      displayUnits,
      nativeUnits,
      dimensionalPrecision,
      displayScale,
      messageFillStyle: 'black',
      messageHeader: 'W: ',
      messageHeaderFillStyle: 'green',
      backgroundFillStyle,
    });
    this.heightObject.makeSpriteMaterial({
      displayUnits,
      nativeUnits,
      dimensionalPrecision,
      displayScale,
      messageFillStyle: 'black',
      messageHeader: 'H: ',
      messageHeaderFillStyle: 'blue',
      backgroundFillStyle,
    });
    this._paintParams = OptimalBoundingBox.getPaintParams({
      displayScale,
      displayUnits,
      nativeUnits,
      dimensionalPrecision,
    });
  }

  public get paintParams(): string {
    return this._paintParams;
  }

  public scaleForOrthographicCamera({
    viewport,
  }: {
    viewport: ViewPort;
  }): void {
    this.lengthObject.scaleForOrthographicCamera({ viewport });
    this.widthObject.scaleForOrthographicCamera({ viewport });
    this.heightObject.scaleForOrthographicCamera({ viewport });
  }

  static getPaintParams({
    displayScale,
    displayUnits,
    nativeUnits,
    dimensionalPrecision,
  }: {
    displayScale: number;
    displayUnits: UNITS;
    nativeUnits: UNITS;
    dimensionalPrecision: number;
  }): string {
    return `${displayScale}${displayUnits}${nativeUnits}${dimensionalPrecision}`;
  }
}
